import { Canvas, useFrame, useThree } from '@react-three/fiber';
import type CameraControlsImpl from 'camera-controls';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import * as THREE from 'three';
import { type Group, PCFSoftShadowMap } from 'three';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';

import {
  applyPlayerPerspectiveClippingForFraming,
  getFitPlayerCameraPoseForBox,
} from '@cartwhl/3d/player';
import Grid from '@cartwhl/3d/r3f/components/Grid';
import Text from '@cartwhl/ui/base/TextNew';

import {
  type BoneColorPreset,
  CARTOGRAPHER_AUTO_GRID_CELL_SIZE,
  type GizmoRotationAxis,
  type PropertiesToolMode,
  type RotateSpaceMode,
  type ViewerToolMode,
  type ViewerVisibility,
} from '../../../cartographerStore';
import { buildAssignedBoneIndexSet } from '../../../lib/assignedBoneSet';
import type { MappingResult } from '../../../lib/recognize/config';
import type { Skeleton } from '../../../lib/skeleton/core';
import type {
  BoneAssignmentsViewerFocus,
  BoneAssignmentsViewerPickMode,
  HoveredBoneAssignmentChain,
} from '../../BoneAssignments';
import JointOffsetManipulator from '../../Manipulators/Properties/JointOffsets';
import PreferredAnglesManipulator from '../../Manipulators/Properties/PreferredAngles';
import RotationLimitsManipulator from '../../Manipulators/Properties/RotationLimits';
import RotationManipulator from '../../Manipulators/Rotation';
import SelectionOrientationManipulator from '../../Manipulators/SelectionOrientation';
import { applySkeletonPoseToSceneBones } from '../viewerPoseBridge';
import CartographerSceneSetup from './CartographerSceneSetup';
import SkeletonOverlay from './SkeletonOverlay';

const SKELETON_MESH_DIAGONAL_RATIO_MIN = 0.55;
const SKELETON_MESH_DIAGONAL_RATIO_MAX = 1.9;
const SKELETON_MESH_CENTER_OFFSET_RATIO_MAX = 0.2;
const FRAME_TARGET_Y_BIAS_FACTOR = 0.08;
const FRAME_DISTANCE_MULTIPLIER = 1.0;
const FRAME_VIEW_DIRECTION = new THREE.Vector3(0, 0, 1);
const FRAME_SETTLE_COUNT = 10;
const FOCUS_TARGET_MIN_BOX_EDGE = 0.18;
const FOCUS_TARGET_SCENE_SIZE_RATIO = 2 / 3;
const BONE_FOCUS_TARGET_VERTICAL_PADDING = 0.03;
const CHAIN_FOCUS_TARGET_VERTICAL_PADDING = 0.035;
const BONE_FOCUS_TARGET_DISTANCE_MULTIPLIER = 1.0;
const CHAIN_FOCUS_TARGET_DISTANCE_MULTIPLIER = 0.98;
const FOLLOW_CAMERA_TARGET_DEAD_ZONE_MIN = 0.001;
const FOLLOW_CAMERA_TARGET_DEAD_ZONE_OFFSET_RATIO = 0.005;
const FOLLOW_CAMERA_TARGET_SMOOTHING_RATE = 36;
const SNAPSHOT_FROST_BLUR_PX = 6;
const THUMBNAIL_CAPTURE_MAX_EDGE_PX = 896;
const AUTO_GRID_CELL_SIZE_MIN = 0.14;
const AUTO_GRID_CELL_SIZE_RADIUS_RATIO = 0.25;
const GRID_SECTION_SIZE_RATIO = 5;
const GRID_PLANE_SIZE = 8;
const GRID_FADE_DISTANCE_MIN = 14;
const GRID_FADE_DISTANCE_SCENE_RADIUS_RATIO = 6;
const GRID_FADE_DISTANCE_CAMERA_DISTANCE_RATIO = 2.5;
const GRID_FADE_DISTANCE_UPDATE_RATIO = 0.02;

const DEFAULT_GRAY_MESH_COLOR = 0xcccccc;
const DEFAULT_GRAY_MESH_METALNESS = 0.15;
const DEFAULT_GRAY_MESH_ROUGHNESS = 0.8;
const FLAT_WHITE_COLOR_MIN = 0.98;

export type ViewerFocusTarget =
  | {
      kind: 'bone-assignments';
      focus: BoneAssignmentsViewerFocus;
    }
  | {
      kind: 'bone';
      boneIndex: number;
    }
  | {
      kind: 'scene';
    };

export type ViewerFocusRequest = {
  token: number;
  target: ViewerFocusTarget;
  fallbackBoneIndex: number | null;
};

type ViewerFollowSeed = {
  token: number;
  offset: THREE.Vector3;
};

function createDefaultGrayMaterial(
  sourceMaterial: THREE.Material,
  isSkinnedMesh: boolean,
  options: { forceOpaque?: boolean } = {},
): THREE.MeshStandardMaterial {
  const grayMaterial = new THREE.MeshStandardMaterial({
    color: DEFAULT_GRAY_MESH_COLOR,
    metalness: DEFAULT_GRAY_MESH_METALNESS,
    roughness: DEFAULT_GRAY_MESH_ROUGHNESS,
  });

  grayMaterial.name = `${sourceMaterial.name || 'material'}__cartographer_gray`;
  grayMaterial.side = sourceMaterial.side;
  if (options.forceOpaque) {
    grayMaterial.transparent = false;
    grayMaterial.opacity = 1;
    grayMaterial.alphaTest = 0;
    grayMaterial.depthWrite = true;
    grayMaterial.depthTest = true;
  } else {
    grayMaterial.transparent = sourceMaterial.transparent;
    grayMaterial.opacity = sourceMaterial.opacity;
    grayMaterial.alphaTest = sourceMaterial.alphaTest;
    grayMaterial.depthWrite = sourceMaterial.depthWrite;
    grayMaterial.depthTest = sourceMaterial.depthTest;
  }
  grayMaterial.colorWrite = sourceMaterial.colorWrite;
  grayMaterial.toneMapped = sourceMaterial.toneMapped;

  if (isSkinnedMesh) {
    (grayMaterial as THREE.Material & { skinning?: boolean }).skinning = true;
  }

  return grayMaterial;
}

function hasMaterialTexture(material: THREE.Material): boolean {
  for (const value of Object.values(material)) {
    if (value instanceof THREE.Texture) return true;
  }

  const uniforms = (material as THREE.ShaderMaterial).uniforms;
  if (!uniforms) return false;

  for (const uniform of Object.values(uniforms)) {
    if (uniform?.value instanceof THREE.Texture) return true;
  }

  return false;
}

function isFlatWhiteColorAttribute(
  attribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
): boolean {
  if (attribute.itemSize < 3) return false;

  for (let i = 0; i < attribute.count; i += 1) {
    if (
      attribute.getX(i) < FLAT_WHITE_COLOR_MIN ||
      attribute.getY(i) < FLAT_WHITE_COLOR_MIN ||
      attribute.getZ(i) < FLAT_WHITE_COLOR_MIN
    ) {
      return false;
    }
  }

  return true;
}

function isFlatWhiteUnlitMaterial(
  mesh: THREE.Mesh,
  material: THREE.Material,
): boolean {
  if (!(material instanceof THREE.MeshBasicMaterial)) return false;
  if (hasMaterialTexture(material)) return false;
  if (
    material.color.r < FLAT_WHITE_COLOR_MIN ||
    material.color.g < FLAT_WHITE_COLOR_MIN ||
    material.color.b < FLAT_WHITE_COLOR_MIN
  ) {
    return false;
  }
  if (!material.vertexColors) return true;

  const colorAttribute = mesh.geometry.getAttribute('color');
  if (
    !(
      colorAttribute instanceof THREE.BufferAttribute ||
      colorAttribute instanceof THREE.InterleavedBufferAttribute
    )
  ) {
    return false;
  }

  return isFlatWhiteColorAttribute(colorAttribute);
}

function applyGrayMaterialOverride(
  scene: Group,
  mode: 'all' | 'flat-white-unlit',
): THREE.Material[] {
  const createdMaterials: THREE.Material[] = [];

  scene.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;

    const isSkinnedMesh = object instanceof THREE.SkinnedMesh;
    const shouldReplaceMaterial = (sourceMaterial: THREE.Material) =>
      mode === 'all' || isFlatWhiteUnlitMaterial(object, sourceMaterial);
    const shouldForceOpaqueMaterial = (sourceMaterial: THREE.Material) =>
      isFlatWhiteUnlitMaterial(object, sourceMaterial);

    if (Array.isArray(object.material)) {
      const nextMaterials = object.material.map((sourceMaterial) => {
        if (!shouldReplaceMaterial(sourceMaterial)) return sourceMaterial;

        const grayMaterial = createDefaultGrayMaterial(
          sourceMaterial,
          isSkinnedMesh,
          { forceOpaque: shouldForceOpaqueMaterial(sourceMaterial) },
        );
        createdMaterials.push(grayMaterial);
        return grayMaterial;
      });
      object.material = nextMaterials;
      return;
    }

    if (!shouldReplaceMaterial(object.material)) return;

    // Textureless unlit white exports are technically valid, but in Cartographer
    // they collapse into a silhouette. Keep the display fallback opaque so the
    // mesh does not render after bones and gizmos in Three's transparent pass.
    const grayMaterial = createDefaultGrayMaterial(
      object.material,
      isSkinnedMesh,
      { forceOpaque: shouldForceOpaqueMaterial(object.material) },
    );
    createdMaterials.push(grayMaterial);
    object.material = grayMaterial;
  });

  return createdMaterials;
}

function isFiniteVector3(v: THREE.Vector3): boolean {
  return Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z);
}

function isUsableBounds(box: THREE.Box3): boolean {
  if (box.isEmpty()) return false;
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  if (!isFiniteVector3(size) || !isFiniteVector3(center)) return false;
  return size.lengthSq() > 1e-12;
}

function resolveGridCellSize(
  gridCellSize: number,
  sceneRadius: number,
): number {
  const safeSceneRadius =
    Number.isFinite(sceneRadius) && sceneRadius > 0 ? sceneRadius : 0;
  if (gridCellSize > CARTOGRAPHER_AUTO_GRID_CELL_SIZE) return gridCellSize;
  return Math.max(
    AUTO_GRID_CELL_SIZE_MIN,
    safeSceneRadius * AUTO_GRID_CELL_SIZE_RADIUS_RATIO,
  );
}

function resolveGridFadeDistance(
  sceneRadius: number,
  cameraDistance?: number,
): number {
  const safeSceneRadius =
    Number.isFinite(sceneRadius) && sceneRadius > 0 ? sceneRadius : 0;
  const safeCameraDistance =
    Number.isFinite(cameraDistance) && cameraDistance !== undefined
      ? cameraDistance
      : undefined;
  const baseFadeDistance = Math.max(
    GRID_FADE_DISTANCE_MIN,
    safeSceneRadius * GRID_FADE_DISTANCE_SCENE_RADIUS_RATIO,
  );
  if (safeCameraDistance === undefined) {
    return baseFadeDistance;
  }
  return Math.max(
    baseFadeDistance,
    safeCameraDistance * GRID_FADE_DISTANCE_CAMERA_DISTANCE_RATIO,
  );
}

function hasMeaningfulGridFadeDistanceChange(
  currentFadeDistance: number,
  nextFadeDistance: number,
): boolean {
  return (
    Math.abs(nextFadeDistance - currentFadeDistance) >=
    Math.max(
      1,
      Math.max(currentFadeDistance, nextFadeDistance) *
        GRID_FADE_DISTANCE_UPDATE_RATIO,
    )
  );
}

function toBlobOrThrow(
  canvas: HTMLCanvasElement,
  mimeType: string,
): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
      mimeType,
    );
  });
}

function getCappedCaptureSize(
  width: number,
  height: number,
  maxEdgePx: number,
): { width: number; height: number } {
  const normalizedWidth = Math.max(1, Math.round(width));
  const normalizedHeight = Math.max(1, Math.round(height));
  const maxCurrentEdge = Math.max(normalizedWidth, normalizedHeight);

  if (maxCurrentEdge <= maxEdgePx) {
    return { width: normalizedWidth, height: normalizedHeight };
  }

  const scale = maxEdgePx / maxCurrentEdge;
  return {
    width: Math.max(1, Math.round(normalizedWidth * scale)),
    height: Math.max(1, Math.round(normalizedHeight * scale)),
  };
}

async function captureThumbnailBlob(
  sourceCanvas: HTMLCanvasElement,
): Promise<Blob> {
  if (sourceCanvas.width <= 0 || sourceCanvas.height <= 0) {
    throw new Error('Canvas has no pixels');
  }

  const { width: targetWidth, height: targetHeight } = getCappedCaptureSize(
    sourceCanvas.width,
    sourceCanvas.height,
    THUMBNAIL_CAPTURE_MAX_EDGE_PX,
  );

  if (
    targetWidth === sourceCanvas.width &&
    targetHeight === sourceCanvas.height
  ) {
    return toBlobOrThrow(sourceCanvas, 'image/png');
  }

  const exportCanvas = document.createElement('canvas');
  exportCanvas.width = targetWidth;
  exportCanvas.height = targetHeight;

  const context = exportCanvas.getContext('2d');
  if (!context) throw new Error('No 2d context');

  context.drawImage(sourceCanvas, 0, 0, targetWidth, targetHeight);

  return toBlobOrThrow(exportCanvas, 'image/png');
}

function resolveAssignedRoleBoneIndex(
  mapping: MappingResult | null,
  skeleton: Skeleton | null,
  roleName: string,
): number | null {
  if (!mapping || !skeleton) return null;
  const boneRef = mapping.roleToBone[roleName];
  if (!boneRef) return null;
  return skeleton.resolveBoneRef(boneRef, { namespace: 'either' });
}

function getCharacterFramingExcludedBoneIndices(
  skeleton: Skeleton | null,
  mapping: MappingResult | null,
): Set<number> | null {
  if (!skeleton) return null;
  const pelvisIndex = resolveAssignedRoleBoneIndex(mapping, skeleton, 'Pelvis');
  if (pelvisIndex === null) return null;
  if (pelvisIndex < 0 || pelvisIndex >= skeleton.bones.length) return null;

  const excluded = new Set<number>();
  const included = new Set<number>();
  const pending = [pelvisIndex];

  while (pending.length > 0) {
    const boneIndex = pending.pop();
    if (boneIndex === undefined) continue;
    if (included.has(boneIndex)) continue;

    const bone = skeleton.bones[boneIndex];
    if (!bone) continue;

    included.add(boneIndex);
    for (const childIndex of bone.children) {
      if (childIndex >= 0 && childIndex < skeleton.bones.length) {
        pending.push(childIndex);
      }
    }
  }

  if (included.size === 0) return null;

  const rootIndex = resolveAssignedRoleBoneIndex(mapping, skeleton, 'Root');

  for (const bone of skeleton.bones) {
    if (
      !included.has(bone.index) ||
      (rootIndex !== null &&
        rootIndex !== pelvisIndex &&
        bone.index === rootIndex)
    ) {
      excluded.add(bone.index);
    }
  }

  return excluded;
}

function computeSkeletonBounds(
  skeleton: Skeleton | null,
  excludedBoneIndices?: ReadonlySet<number> | null,
): THREE.Box3 | null {
  const box = new THREE.Box3();
  if (!skeleton || skeleton.bones.length === 0) return null;
  let hasBounds = false;

  for (const bone of skeleton.bones) {
    if (excludedBoneIndices?.has(bone.index)) continue;
    const e = bone.posedWorld.elements;
    const x = e[12];
    const y = e[13];
    const z = e[14];
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
      continue;
    }
    box.expandByPoint(new THREE.Vector3(x, y, z));
    hasBounds = true;
  }

  if (!hasBounds || !isUsableBounds(box)) return null;
  return box;
}

function computeMeshBounds(scene: Group): THREE.Box3 | null {
  const skinnedMeshBox = new THREE.Box3();
  let hasSkinnedMesh = false;

  scene.traverse((object) => {
    if (!(object instanceof THREE.SkinnedMesh)) return;
    skinnedMeshBox.expandByObject(object, true);
    hasSkinnedMesh = true;
  });

  if (hasSkinnedMesh && isUsableBounds(skinnedMeshBox)) {
    return skinnedMeshBox;
  }

  const meshBox = new THREE.Box3().setFromObject(scene);
  return isUsableBounds(meshBox) ? meshBox : null;
}

function computeSceneBounds(
  scene: Group,
  skeleton: Skeleton | null,
  mapping: MappingResult | null,
): THREE.Box3 {
  const skeletonBounds = computeSkeletonBounds(
    skeleton,
    getCharacterFramingExcludedBoneIndices(skeleton, mapping),
  );
  const meshBounds = computeMeshBounds(scene);

  if (!skeletonBounds && meshBounds) return meshBounds;
  if (skeletonBounds && !meshBounds) return skeletonBounds;
  if (!skeletonBounds && !meshBounds) return new THREE.Box3();
  if (!skeletonBounds || !meshBounds) return new THREE.Box3();

  if (skeletonBounds != null && meshBounds != null) {
    const skeletonDiag = skeletonBounds.getSize(new THREE.Vector3()).length();
    const meshDiag = meshBounds.getSize(new THREE.Vector3()).length();

    if (!Number.isFinite(skeletonDiag) || skeletonDiag <= 1e-6) {
      return meshBounds;
    }
    if (!Number.isFinite(meshDiag) || meshDiag <= 1e-6) {
      return skeletonBounds;
    }

    const ratio = skeletonDiag / meshDiag;
    const skeletonCenter = skeletonBounds.getCenter(new THREE.Vector3());
    const meshCenter = meshBounds.getCenter(new THREE.Vector3());
    const centerOffsetRatio =
      skeletonCenter.distanceTo(meshCenter) / Math.max(skeletonDiag, meshDiag);
    if (!Number.isFinite(ratio) || !Number.isFinite(centerOffsetRatio)) {
      return meshBounds;
    }

    if (
      ratio < SKELETON_MESH_DIAGONAL_RATIO_MIN ||
      ratio > SKELETON_MESH_DIAGONAL_RATIO_MAX ||
      centerOffsetRatio > SKELETON_MESH_CENTER_OFFSET_RATIO_MAX
    ) {
      return meshBounds;
    }

    const framingBounds = skeletonBounds.clone().union(meshBounds);
    const maxHorizontalOffset = Math.max(
      -framingBounds.min.x,
      framingBounds.max.x,
    );
    framingBounds.min.x = -maxHorizontalOffset;
    framingBounds.max.x = maxHorizontalOffset;
    return framingBounds;
  }

  return skeletonBounds ?? meshBounds ?? new THREE.Box3();
}

function getBoxTargetCenter(
  box: THREE.Box3,
  targetYBiasFactor = 0,
): THREE.Vector3 {
  const target = box.getCenter(new THREE.Vector3());
  if (targetYBiasFactor !== 0) {
    const size = box.getSize(new THREE.Vector3());
    target.y += size.y * targetYBiasFactor;
  }
  return target;
}

function buildBoxFromPoints(
  points: readonly THREE.Vector3[],
): THREE.Box3 | null {
  const box = new THREE.Box3();
  let hasBounds = false;

  for (const point of points) {
    if (!isFiniteVector3(point)) continue;
    box.expandByPoint(point);
    hasBounds = true;
  }

  return hasBounds ? box : null;
}

function getMinimumFocusTargetBoxSize(sceneBounds: THREE.Box3): THREE.Vector3 {
  if (!isUsableBounds(sceneBounds)) {
    return new THREE.Vector3(
      FOCUS_TARGET_MIN_BOX_EDGE,
      FOCUS_TARGET_MIN_BOX_EDGE,
      FOCUS_TARGET_MIN_BOX_EDGE,
    );
  }
  const size = sceneBounds.getSize(new THREE.Vector3());
  if (!isFiniteVector3(size)) {
    return new THREE.Vector3(
      FOCUS_TARGET_MIN_BOX_EDGE,
      FOCUS_TARGET_MIN_BOX_EDGE,
      FOCUS_TARGET_MIN_BOX_EDGE,
    );
  }
  return new THREE.Vector3(
    Math.max(size.x * FOCUS_TARGET_SCENE_SIZE_RATIO, FOCUS_TARGET_MIN_BOX_EDGE),
    Math.max(size.y * FOCUS_TARGET_SCENE_SIZE_RATIO, FOCUS_TARGET_MIN_BOX_EDGE),
    Math.max(size.z * FOCUS_TARGET_SCENE_SIZE_RATIO, FOCUS_TARGET_MIN_BOX_EDGE),
  );
}

function ensureMinimumBoxSize(
  box: THREE.Box3,
  minimumSize: THREE.Vector3,
): THREE.Box3 | null {
  if (box.isEmpty()) return null;
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  if (
    !isFiniteVector3(center) ||
    !isFiniteVector3(size) ||
    !isFiniteVector3(minimumSize)
  ) {
    return null;
  }
  const nextSize = new THREE.Vector3(
    Math.max(size.x, minimumSize.x),
    Math.max(size.y, minimumSize.y),
    Math.max(size.z, minimumSize.z),
  );
  return new THREE.Box3().setFromCenterAndSize(center, nextSize);
}

function buildFocusBoundsFromPoints(
  points: readonly THREE.Vector3[],
  sceneBounds: THREE.Box3,
): THREE.Box3 | null {
  const box = buildBoxFromPoints(points);
  if (!box) return null;
  return ensureMinimumBoxSize(box, getMinimumFocusTargetBoxSize(sceneBounds));
}

function resolveBoneFocusBounds(
  skeleton: Skeleton | null,
  boneIndex: number | null,
  sceneBounds: THREE.Box3,
): THREE.Box3 | null {
  if (!skeleton || boneIndex === null) return null;
  if (boneIndex < 0 || boneIndex >= skeleton.bones.length) return null;
  return buildFocusBoundsFromPoints(
    [skeleton.getBonePosedWorldPosition(boneIndex)],
    sceneBounds,
  );
}

function resolveBoneAssignmentsFocusBounds(
  focus: BoneAssignmentsViewerFocus,
  mapping: MappingResult | null,
  skeleton: Skeleton | null,
  sceneBounds: THREE.Box3,
): THREE.Box3 | null {
  if (!mapping || !skeleton) return null;

  if (focus.mode === 'roles') {
    const roleBoneRef = mapping.roleToBone[focus.roleName];
    if (!roleBoneRef) return null;
    const roleBoneIndex = skeleton.resolveBoneRef(roleBoneRef, {
      namespace: 'either',
    });
    return resolveBoneFocusBounds(skeleton, roleBoneIndex, sceneBounds);
  }

  const chainBoneRefs =
    mapping.chainResults?.[focus.chainName]?.boneNames ??
    mapping.chains?.[focus.chainName] ??
    [];
  if (chainBoneRefs.length === 0) return null;

  const chainPoints = chainBoneRefs
    .map((boneRef) =>
      skeleton.resolveBoneRef(boneRef, {
        namespace: 'either',
      }),
    )
    .filter((boneIndex): boneIndex is number => boneIndex !== null)
    .map((boneIndex) => skeleton.getBonePosedWorldPosition(boneIndex));

  return buildFocusBoundsFromPoints(chainPoints, sceneBounds);
}

function resolveFocusFramingTarget({
  request,
  scene,
  skeleton,
  mapping,
}: {
  request: ViewerFocusRequest;
  scene: Group;
  skeleton: Skeleton | null;
  mapping: MappingResult | null;
}): {
  box: THREE.Box3;
  targetYBiasFactor: number;
  distanceMultiplier: number;
  verticalPadding: number;
} | null {
  const sceneBounds = computeSceneBounds(scene, skeleton, mapping);

  if (request.target.kind === 'bone-assignments') {
    const isChainFocus = request.target.focus.mode === 'chains';
    const focusBounds = resolveBoneAssignmentsFocusBounds(
      request.target.focus,
      mapping,
      skeleton,
      sceneBounds,
    );
    if (focusBounds) {
      return {
        box: focusBounds,
        targetYBiasFactor: 0,
        distanceMultiplier: isChainFocus
          ? CHAIN_FOCUS_TARGET_DISTANCE_MULTIPLIER
          : BONE_FOCUS_TARGET_DISTANCE_MULTIPLIER,
        verticalPadding: isChainFocus
          ? CHAIN_FOCUS_TARGET_VERTICAL_PADDING
          : BONE_FOCUS_TARGET_VERTICAL_PADDING,
      };
    }

    const fallbackBoneBounds = resolveBoneFocusBounds(
      skeleton,
      request.fallbackBoneIndex,
      sceneBounds,
    );
    if (fallbackBoneBounds) {
      return {
        box: fallbackBoneBounds,
        targetYBiasFactor: 0,
        distanceMultiplier: BONE_FOCUS_TARGET_DISTANCE_MULTIPLIER,
        verticalPadding: BONE_FOCUS_TARGET_VERTICAL_PADDING,
      };
    }
  } else if (request.target.kind === 'bone') {
    const focusBounds = resolveBoneFocusBounds(
      skeleton,
      request.target.boneIndex,
      sceneBounds,
    );
    if (focusBounds) {
      return {
        box: focusBounds,
        targetYBiasFactor: 0,
        distanceMultiplier: BONE_FOCUS_TARGET_DISTANCE_MULTIPLIER,
        verticalPadding: BONE_FOCUS_TARGET_VERTICAL_PADDING,
      };
    }
  }

  if (!isUsableBounds(sceneBounds)) return null;
  return {
    box: sceneBounds,
    targetYBiasFactor: FRAME_TARGET_Y_BIAS_FACTOR,
    distanceMultiplier: FRAME_DISTANCE_MULTIPLIER,
    verticalPadding: 0,
  };
}

function computeFollowSceneBounds(
  scene: Group,
  skeleton: Skeleton | null,
  mapping: MappingResult | null,
): THREE.Box3 | null {
  const skeletonBounds = computeSkeletonBounds(
    skeleton,
    getCharacterFramingExcludedBoneIndices(skeleton, mapping),
  );
  if (skeletonBounds && isUsableBounds(skeletonBounds)) {
    return skeletonBounds;
  }
  const meshBounds = computeMeshBounds(scene);
  return meshBounds && isUsableBounds(meshBounds) ? meshBounds : null;
}

function resolveFollowMotionTarget({
  request,
  scene,
  skeleton,
  mapping,
}: {
  request: ViewerFocusRequest;
  scene: Group;
  skeleton: Skeleton | null;
  mapping: MappingResult | null;
}): { key: string; target: THREE.Vector3 } | null {
  if (request.target.kind === 'bone-assignments') {
    const focus = request.target.focus;
    if (focus.mode === 'roles') {
      const roleBoneRef = mapping?.roleToBone[focus.roleName];
      const roleBoneIndex =
        roleBoneRef && skeleton
          ? skeleton.resolveBoneRef(roleBoneRef, {
              namespace: 'either',
            })
          : null;
      if (roleBoneIndex !== null && skeleton) {
        return {
          key: `${request.token}:role:${focus.roleName}`,
          target: skeleton.getBonePosedWorldPosition(roleBoneIndex),
        };
      }
    } else {
      const chainBoneRefs =
        mapping?.chainResults?.[focus.chainName]?.boneNames ??
        mapping?.chains?.[focus.chainName] ??
        [];
      if (chainBoneRefs.length > 0 && skeleton) {
        const chainBox = buildBoxFromPoints(
          chainBoneRefs
            .map((boneRef) =>
              skeleton.resolveBoneRef(boneRef, {
                namespace: 'either',
              }),
            )
            .filter((boneIndex): boneIndex is number => boneIndex !== null)
            .map((boneIndex) => skeleton.getBonePosedWorldPosition(boneIndex)),
        );
        if (chainBox) {
          return {
            key: `${request.token}:chain:${focus.chainName}`,
            target: getBoxTargetCenter(chainBox),
          };
        }
      }
    }

    if (request.fallbackBoneIndex !== null && skeleton) {
      return {
        key: `${request.token}:fallback-bone:${request.fallbackBoneIndex}`,
        target: skeleton.getBonePosedWorldPosition(request.fallbackBoneIndex),
      };
    }
  } else if (request.target.kind === 'bone') {
    if (
      skeleton &&
      request.target.boneIndex >= 0 &&
      request.target.boneIndex < skeleton.bones.length
    ) {
      return {
        key: `${request.token}:bone:${request.target.boneIndex}`,
        target: skeleton.getBonePosedWorldPosition(request.target.boneIndex),
      };
    }
  }

  const sceneBounds = computeFollowSceneBounds(scene, skeleton, mapping);
  if (!sceneBounds) return null;
  return {
    key: `${request.token}:scene`,
    target: getBoxTargetCenter(sceneBounds, FRAME_TARGET_Y_BIAS_FACTOR),
  };
}

function AutoFrame({
  autoFrameReady,
  autoFrameToken,
  scene,
  skeleton,
  mapping,
  onFrameSettled,
  onFrameDistanceChange,
  onFramingSphereRadiusChange,
}: {
  autoFrameReady: boolean;
  autoFrameToken: number;
  scene: Group;
  skeleton: Skeleton | null;
  mapping: MappingResult | null;
  onFrameSettled?: () => void;
  onFrameDistanceChange?: (distance: number) => void;
  onFramingSphereRadiusChange?: (radius: number) => void;
}) {
  const controls = useThree(
    (state) => state.controls,
  ) as CameraControlsImpl | null;
  const camera = useThree((state) => state.camera) as THREE.PerspectiveCamera;
  const framedSceneKeyRef = useRef<string | null>(null);
  const framingCountdownRef = useRef(0);
  const framingDataRef = useRef<{
    position: THREE.Vector3;
    target: THREE.Vector3;
  } | null>(null);
  const onFrameSettledRef = useRef(onFrameSettled);
  onFrameSettledRef.current = onFrameSettled;

  useEffect(() => {
    if (!controls || !autoFrameReady) return;
    // Auto-frame only once after a file finishes loading. Mapping edits/runs
    // within the same session should preserve the user's camera.
    const frameKey = `${autoFrameToken}:${scene.uuid}`;
    if (framedSceneKeyRef.current === frameKey) return;

    scene.updateMatrixWorld(true);
    const box = computeSceneBounds(scene, skeleton, mapping);
    const pose = getFitPlayerCameraPoseForBox(camera, box, {
      fitStrategy: 'box',
      targetYBiasFactor: FRAME_TARGET_Y_BIAS_FACTOR,
      distanceMultiplier: FRAME_DISTANCE_MULTIPLIER,
      verticalPadding: 0,
      viewDirection: FRAME_VIEW_DIRECTION,
    });
    if (!pose) return;

    applyPlayerPerspectiveClippingForFraming(
      camera,
      pose.position,
      pose.target,
      pose.framingSphereRadius,
    );
    onFrameDistanceChange?.(pose.position.distanceTo(pose.target));
    onFramingSphereRadiusChange?.(pose.framingSphereRadius);

    framingDataRef.current = {
      position: pose.position,
      target: pose.target,
    };
    framingCountdownRef.current = FRAME_SETTLE_COUNT;
    framedSceneKeyRef.current = frameKey;
  }, [
    autoFrameReady,
    autoFrameToken,
    camera,
    controls,
    mapping,
    onFrameDistanceChange,
    onFramingSphereRadiusChange,
    scene,
    skeleton,
  ]);

  useFrame(() => {
    if (
      framingCountdownRef.current <= 0 ||
      !framingDataRef.current ||
      !controls
    )
      return;
    framingCountdownRef.current--;
    const { position, target } = framingDataRef.current;
    void controls.setLookAt(
      position.x,
      position.y,
      position.z,
      target.x,
      target.y,
      target.z,
      false,
    );
    if (framingCountdownRef.current === 0) {
      framingDataRef.current = null;
      onFrameSettledRef.current?.();
    }
  });

  return null;
}

function FocusFrame({
  request,
  scene,
  skeleton,
  mapping,
  onFramed,
  onFramingSphereRadiusChange,
}: {
  request: ViewerFocusRequest | null;
  scene: Group;
  skeleton: Skeleton | null;
  mapping: MappingResult | null;
  onFramed?: (seed: ViewerFollowSeed | null) => void;
  onFramingSphereRadiusChange?: (radius: number) => void;
}) {
  const controls = useThree(
    (state) => state.controls,
  ) as CameraControlsImpl | null;
  const camera = useThree((state) => state.camera) as THREE.PerspectiveCamera;
  const handledRequestTokenRef = useRef<number | null>(null);

  useEffect(() => {
    if (!request || !controls) return;
    if (handledRequestTokenRef.current === request.token) return;

    scene.updateMatrixWorld(true);
    const framingTarget = resolveFocusFramingTarget({
      request,
      scene,
      skeleton,
      mapping,
    });
    if (!framingTarget) return;

    const pose = getFitPlayerCameraPoseForBox(camera, framingTarget.box, {
      fitStrategy: 'box',
      targetYBiasFactor: framingTarget.targetYBiasFactor,
      distanceMultiplier: framingTarget.distanceMultiplier,
      verticalPadding: framingTarget.verticalPadding,
    });
    if (!pose) return;

    applyPlayerPerspectiveClippingForFraming(
      camera,
      pose.position,
      pose.target,
      pose.framingSphereRadius,
    );
    onFramingSphereRadiusChange?.(pose.framingSphereRadius);
    void controls.setLookAt(
      pose.position.x,
      pose.position.y,
      pose.position.z,
      pose.target.x,
      pose.target.y,
      pose.target.z,
      true,
    );
    onFramed?.({
      token: request.token,
      offset: pose.position.clone().sub(pose.target),
    });
    handledRequestTokenRef.current = request.token;
  }, [
    camera,
    controls,
    mapping,
    onFramed,
    onFramingSphereRadiusChange,
    request,
    scene,
    skeleton,
  ]);

  return null;
}

function CameraClippingSync({
  framingSphereRadius,
}: {
  framingSphereRadius: number;
}) {
  const controls = useThree(
    (state) => state.controls,
  ) as CameraControlsImpl | null;
  const camera = useThree((state) => state.camera) as THREE.PerspectiveCamera;
  const targetScratchRef = useRef(new THREE.Vector3());

  useFrame(() => {
    if (!controls || !Number.isFinite(framingSphereRadius)) return;
    const orbitTarget = controls.getTarget(targetScratchRef.current);
    applyPlayerPerspectiveClippingForFraming(
      camera,
      camera.position,
      orbitTarget,
      framingSphereRadius,
    );
  });

  return null;
}

function SceneGrid({
  gridCellSize,
  sceneRadius,
  normalizationKey,
}: {
  gridCellSize: number;
  sceneRadius: number;
  normalizationKey: string;
}) {
  const controls = useThree(
    (state) => state.controls,
  ) as CameraControlsImpl | null;
  const camera = useThree((state) => state.camera) as THREE.PerspectiveCamera;
  const targetScratchRef = useRef(new THREE.Vector3());
  const normalizedSceneKeyRef = useRef<string | null>(null);
  const [normalizedSceneRadius, setNormalizedSceneRadius] = useState(sceneRadius);

  useEffect(() => {
    if (!Number.isFinite(sceneRadius) || sceneRadius <= 0) return;
    if (normalizedSceneKeyRef.current === normalizationKey) return;

    // Motion playback mutates pose bounds over time, but the auto-normalized grid
    // should stay stable for the current loaded character until a new scene loads.
    normalizedSceneKeyRef.current = normalizationKey;
    setNormalizedSceneRadius(sceneRadius);
  }, [normalizationKey, sceneRadius]);

  const resolvedGridCellSize = useMemo(
    () => resolveGridCellSize(gridCellSize, normalizedSceneRadius),
    [gridCellSize, normalizedSceneRadius],
  );
  const [fadeDistance, setFadeDistance] = useState(() =>
    resolveGridFadeDistance(normalizedSceneRadius),
  );
  const fadeDistanceRef = useRef(fadeDistance);

  useEffect(() => {
    const orbitTarget = controls?.getTarget(targetScratchRef.current);
    const cameraDistance = orbitTarget
      ? camera.position.distanceTo(orbitTarget)
      : undefined;
    const nextFadeDistance = resolveGridFadeDistance(
      normalizedSceneRadius,
      cameraDistance,
    );
    fadeDistanceRef.current = nextFadeDistance;
    setFadeDistance(nextFadeDistance);
  }, [camera, controls, normalizedSceneRadius]);

  useFrame(() => {
    if (!controls) return;
    const orbitTarget = controls.getTarget(targetScratchRef.current);
    const nextFadeDistance = resolveGridFadeDistance(
      normalizedSceneRadius,
      camera.position.distanceTo(orbitTarget),
    );
    if (
      !hasMeaningfulGridFadeDistanceChange(
        fadeDistanceRef.current,
        nextFadeDistance,
      )
    ) {
      return;
    }
    fadeDistanceRef.current = nextFadeDistance;
    setFadeDistance(nextFadeDistance);
  });

  return (
    <Grid
      // Drei's "infinite" grid still lives on a finite plane; enlarging the base
      // geometry keeps wide zoom levels from revealing a square boundary.
      args={[GRID_PLANE_SIZE, GRID_PLANE_SIZE]}
      cellSize={resolvedGridCellSize}
      sectionSize={resolvedGridCellSize * GRID_SECTION_SIZE_RATIO}
      fadeDistance={fadeDistance}
    />
  );
}

function FollowMotionCamera({
  enabled,
  request,
  scene,
  skeleton,
  mapping,
  seed,
}: {
  enabled: boolean;
  request: ViewerFocusRequest | null;
  scene: Group;
  skeleton: Skeleton | null;
  mapping: MappingResult | null;
  seed: ViewerFollowSeed | null;
}) {
  const controls = useThree(
    (state) => state.controls,
  ) as CameraControlsImpl | null;
  const followedTargetKeyRef = useRef<string | null>(null);
  const positionScratchRef = useRef(new THREE.Vector3());
  const targetScratchRef = useRef(new THREE.Vector3());
  const targetDeltaScratchRef = useRef(new THREE.Vector3());
  const desiredTargetScratchRef = useRef(new THREE.Vector3());
  const desiredPositionScratchRef = useRef(new THREE.Vector3());

  useEffect(() => {
    if (enabled) return;
    followedTargetKeyRef.current = null;
  }, [enabled]);

  useFrame((_, delta) => {
    if (!enabled || !request || !controls) return;

    scene.updateMatrixWorld(true);
    const followTarget = resolveFollowMotionTarget({
      request,
      scene,
      skeleton,
      mapping,
    });
    if (!followTarget) return;

    const currentTarget = controls.getTarget(targetScratchRef.current);
    const currentOffset = controls
      .getPosition(positionScratchRef.current)
      .sub(currentTarget);

    let nextOffset = currentOffset;
    if (followedTargetKeyRef.current !== followTarget.key) {
      if (seed && seed.token === request.token) {
        nextOffset = seed.offset.clone();
      }
      followedTargetKeyRef.current = followTarget.key;
    }

    const targetDelta = targetDeltaScratchRef.current.subVectors(
      followTarget.target,
      currentTarget,
    );
    const targetDeltaLength = targetDelta.length();
    const targetDeadZone = Math.max(
      FOLLOW_CAMERA_TARGET_DEAD_ZONE_MIN,
      currentOffset.length() * FOLLOW_CAMERA_TARGET_DEAD_ZONE_OFFSET_RATIO,
    );
    const targetForSmoothing =
      targetDeltaLength <= targetDeadZone
        ? currentTarget
        : targetDelta
            .multiplyScalar(
              (targetDeltaLength - targetDeadZone) / targetDeltaLength,
            )
            .add(currentTarget);
    const smoothingAlpha =
      1 - Math.exp(-delta * FOLLOW_CAMERA_TARGET_SMOOTHING_RATE);
    const desiredTarget = desiredTargetScratchRef.current
      .copy(currentTarget)
      .lerp(targetForSmoothing, smoothingAlpha);
    const desiredPosition = desiredPositionScratchRef.current
      .copy(desiredTarget)
      .add(nextOffset);
    void controls.setLookAt(
      desiredPosition.x,
      desiredPosition.y,
      desiredPosition.z,
      desiredTarget.x,
      desiredTarget.y,
      desiredTarget.z,
      false,
    );
    controls.update(delta);
  });

  return null;
}

function MeshObject({
  scene,
  visible,
  useDefaultShader,
  skeleton,
  poseVersion,
  livePoseUpdate,
  onClonedSceneChange,
}: {
  scene: Group;
  visible: boolean;
  useDefaultShader: boolean;
  skeleton: Skeleton | null;
  poseVersion: number;
  livePoseUpdate: boolean;
  onClonedSceneChange?: (scene: Group | null) => void;
}) {
  const clonedScene = useMemo(() => {
    const c = SkeletonUtils.clone(scene) as Group;
    c.userData.grayOverrideMaterials = applyGrayMaterialOverride(
      c,
      useDefaultShader ? 'all' : 'flat-white-unlit',
    );
    return c;
  }, [scene, useDefaultShader]);

  useEffect(() => {
    if (!skeleton) return;
    applySkeletonPoseToSceneBones(clonedScene, skeleton);
  }, [clonedScene, poseVersion, skeleton]);

  useFrame(() => {
    if (!livePoseUpdate || !skeleton) return;
    applySkeletonPoseToSceneBones(clonedScene, skeleton);
  });

  useEffect(() => {
    onClonedSceneChange?.(clonedScene);
    return () => onClonedSceneChange?.(null);
  }, [clonedScene, onClonedSceneChange]);

  useEffect(() => {
    return () => {
      const grayOverrideMaterials =
        (clonedScene.userData.grayOverrideMaterials as
          | THREE.Material[]
          | undefined) ?? [];
      for (const material of grayOverrideMaterials) {
        material.dispose();
      }
    };
  }, [clonedScene]);

  clonedScene.visible = visible;

  return <primitive object={clonedScene} />;
}

function SceneContent({
  autoFrameReady,
  autoFrameToken,
  scene,
  skeleton,
  mapping,
  focusRequest,
  cameraFollowMotion,
  assignedBoneIndexSet,
  excludedBoneRefs,
  visibility,
  boneColorPreset,
  boneRadiusScale,
  chainThicknessScale,
  manipulatorScale,
  propertiesVersion,
  poseVersion,
  gridCellSize,
  selectedBoneIndex,
  forcedRoleTag,
  boneAssignmentsViewerFocus,
  hoveredBoneAssignmentChain,
  boneAssignmentsViewerPickMode,
  viewerToolMode,
  rotateSpaceMode,
  propertiesToolMode,
  isFramed,
  onFrameSettled,
  onSelectBone,
  onSelectBoneFromPropertiesBadge,
  onBonePropertiesChange,
  onJointOffsetChange,
  onApplyBoneRotateFromWorldQuaternion,
  onApplyBoneRotateFromGimbalDelta,
  onSetBoneEulerRotation,
  onRotateGizmoDraggingChange,
  onPropertiesManipulatorDraggingChange,
  suppressBoneNameLabels,
}: {
  autoFrameReady: boolean;
  autoFrameToken: number;
  scene: Group;
  skeleton: Skeleton | null;
  mapping: MappingResult | null;
  focusRequest: ViewerFocusRequest | null;
  cameraFollowMotion: boolean;
  assignedBoneIndexSet: Set<number> | null;
  excludedBoneRefs: string[];
  visibility: ViewerVisibility;
  boneColorPreset: BoneColorPreset;
  boneRadiusScale: number;
  chainThicknessScale: number;
  manipulatorScale: number;
  propertiesVersion: number;
  poseVersion: number;
  gridCellSize: number;
  selectedBoneIndex: number | null;
  forcedRoleTag: {
    boneIndex: number;
    role: string;
    confidence: number | null;
  } | null;
  boneAssignmentsViewerFocus: BoneAssignmentsViewerFocus | null;
  hoveredBoneAssignmentChain: HoveredBoneAssignmentChain | null;
  boneAssignmentsViewerPickMode: BoneAssignmentsViewerPickMode | null;
  viewerToolMode: ViewerToolMode;
  rotateSpaceMode: RotateSpaceMode;
  propertiesToolMode: PropertiesToolMode;
  isFramed: boolean;
  onFrameSettled: () => void;
  onSelectBone?: (index: number | null) => void;
  onSelectBoneFromPropertiesBadge?: (index: number) => void;
  onBonePropertiesChange?: () => void;
  onJointOffsetChange?: (
    boneRef: string,
    rotation: [number, number, number] | null,
  ) => void;
  onApplyBoneRotateFromWorldQuaternion?: (
    boneIndex: number,
    worldQuaternion: THREE.Quaternion,
  ) => void;
  onApplyBoneRotateFromGimbalDelta?: (
    boneIndex: number,
    axis: GizmoRotationAxis,
    deltaDegrees: number,
  ) => void;
  onSetBoneEulerRotation?: (
    boneIndex: number,
    eulerDegrees: [number, number, number],
  ) => void;
  onRotateGizmoDraggingChange?: (isDragging: boolean) => void;
  onPropertiesManipulatorDraggingChange?: (isDragging: boolean) => void;
  suppressBoneNameLabels: boolean;
}) {
  const [framingScene, setFramingScene] = useState<Group | null>(null);
  const [followSeed, setFollowSeed] = useState<ViewerFollowSeed | null>(null);
  const [sceneFrameDistance, setSceneFrameDistance] = useState<number | null>(
    null,
  );
  const [framingSphereRadius, setFramingSphereRadius] = useState<number | null>(
    null,
  );
  const sceneForBounds = framingScene ?? scene;
  const canRenderContactShadow = scene.getObjectByName('pelvis') !== null;
  const sceneBounds = useMemo(() => {
    // Pose updates mutate the scene graph in place, so keep this memo reactive
    // even when the scene object identity is unchanged.
    void poseVersion;
    return computeSceneBounds(sceneForBounds, skeleton, mapping);
  }, [mapping, poseVersion, sceneForBounds, skeleton]);
  const followRequest = useMemo<ViewerFocusRequest | null>(() => {
    if (focusRequest) return focusRequest;
    if (selectedBoneIndex !== null) {
      return {
        token: 0,
        target: {
          kind: 'bone',
          boneIndex: selectedBoneIndex,
        },
        fallbackBoneIndex: selectedBoneIndex,
      };
    }
    return {
      token: 0,
      target: { kind: 'scene' },
      fallbackBoneIndex: null,
    };
  }, [focusRequest, selectedBoneIndex]);
  const sceneRadius = useMemo(() => {
    if (sceneBounds.isEmpty()) {
      return 5;
    }
    const sphere = new THREE.Sphere();
    sceneBounds.getBoundingSphere(sphere);
    return sphere.radius || 5;
  }, [sceneBounds]);

  const excludedBoneIndexSet = useMemo(
    () =>
      new Set<number>(
        excludedBoneRefs
          .map(
            (ref) =>
              skeleton?.resolveBoneRef(ref, { namespace: 'internal' }) ?? null,
          )
          .filter((idx): idx is number => idx !== null),
      ),
    [excludedBoneRefs, skeleton],
  );
  const selectedBoneExcluded = useMemo(() => {
    if (!skeleton) return false;
    if (selectedBoneIndex === null) return false;
    if (selectedBoneIndex < 0 || selectedBoneIndex >= skeleton.bones.length) {
      return false;
    }
    return excludedBoneIndexSet.has(selectedBoneIndex);
  }, [excludedBoneIndexSet, selectedBoneIndex, skeleton]);
  const showPoseOverlays = !suppressBoneNameLabels;

  return (
    <>
      <CartographerSceneSetup
        showShadow={visibility.showShadow && canRenderContactShadow}
        sceneRadius={sceneRadius}
        sceneFrameDistance={sceneFrameDistance}
        contentVisible={isFramed}
      />

      <AutoFrame
        autoFrameReady={autoFrameReady}
        autoFrameToken={autoFrameToken}
        scene={sceneForBounds}
        skeleton={skeleton}
        mapping={mapping}
        onFrameSettled={onFrameSettled}
        onFrameDistanceChange={setSceneFrameDistance}
        onFramingSphereRadiusChange={setFramingSphereRadius}
      />
      <FocusFrame
        request={focusRequest}
        scene={sceneForBounds}
        skeleton={skeleton}
        mapping={mapping}
        onFramed={setFollowSeed}
        onFramingSphereRadiusChange={setFramingSphereRadius}
      />
      <CameraClippingSync
        framingSphereRadius={framingSphereRadius ?? sceneRadius}
      />
      <FollowMotionCamera
        enabled={cameraFollowMotion}
        request={followRequest}
        scene={sceneForBounds}
        skeleton={skeleton}
        mapping={mapping}
        seed={followSeed}
      />
      <group visible={isFramed}>
        {visibility.showGrid && (
          <SceneGrid
            gridCellSize={gridCellSize}
            sceneRadius={sceneRadius}
            normalizationKey={scene.uuid}
          />
        )}
        <MeshObject
          scene={scene}
          visible={visibility.showMesh}
          useDefaultShader={visibility.useDefaultShader}
          skeleton={skeleton}
          poseVersion={poseVersion}
          livePoseUpdate={suppressBoneNameLabels}
          onClonedSceneChange={setFramingScene}
        />

        {skeleton && showPoseOverlays && (
          <>
            <SkeletonOverlay
              skeleton={skeleton}
              mapping={mapping}
              assignedBoneIndexSet={assignedBoneIndexSet}
              excludedBoneRefs={excludedBoneRefs}
              showBones={visibility.showBones}
              showExcludedBones={visibility.showExcludedBones}
              showUnassignedBones={visibility.showUnassignedBones}
              showChains={visibility.showChains}
              boneColorPreset={boneColorPreset}
              boneRadiusScale={boneRadiusScale}
              chainThicknessScale={chainThicknessScale}
              showBoneNames={
                visibility.showBoneNames && !suppressBoneNameLabels
              }
              showRoles={visibility.showRoles}
              forcedRoleTag={forcedRoleTag}
              boneAssignmentsViewerFocus={boneAssignmentsViewerFocus}
              hoveredBoneAssignmentChain={hoveredBoneAssignmentChain}
              boneAssignmentsViewerPickMode={boneAssignmentsViewerPickMode}
              showProperties={visibility.showProperties}
              propertiesVersion={propertiesVersion}
              poseVersion={poseVersion}
              selectedBoneIndex={selectedBoneIndex}
              onSelectBone={onSelectBone}
              onSelectBoneFromPropertiesBadge={onSelectBoneFromPropertiesBadge}
              selectionStopsPropagation={viewerToolMode === 'select'}
              showSelectionHighlight={viewerToolMode !== 'properties'}
            />
            <SelectionOrientationManipulator
              skeleton={skeleton}
              selectedBoneIndex={selectedBoneIndex}
              visible={
                visibility.showSelectedBoneAxes && viewerToolMode === 'select'
              }
              manipulatorScale={manipulatorScale}
            />
            <RotationManipulator
              skeleton={skeleton}
              selectedBoneIndex={selectedBoneIndex}
              viewerToolMode={viewerToolMode}
              rotateSpaceMode={rotateSpaceMode}
              poseVersion={poseVersion}
              manipulatorScale={manipulatorScale}
              onApplyBoneRotateFromWorldQuaternion={
                onApplyBoneRotateFromWorldQuaternion
              }
              onApplyBoneRotateFromGimbalDelta={
                onApplyBoneRotateFromGimbalDelta
              }
              onDraggingChange={onRotateGizmoDraggingChange}
            />
            <RotationLimitsManipulator
              skeleton={skeleton}
              selectedBoneIndex={selectedBoneIndex}
              enabled={
                viewerToolMode === 'properties' &&
                propertiesToolMode === 'limits' &&
                !selectedBoneExcluded
              }
              poseVersion={poseVersion}
              propertiesVersion={propertiesVersion}
              manipulatorScale={manipulatorScale}
              onBonePropertiesChange={onBonePropertiesChange}
              onApplyBoneRotateFromGimbalDelta={
                onApplyBoneRotateFromGimbalDelta
              }
              onSetBoneEulerRotation={onSetBoneEulerRotation}
              onDraggingChange={onPropertiesManipulatorDraggingChange}
            />
            <PreferredAnglesManipulator
              skeleton={skeleton}
              selectedBoneIndex={selectedBoneIndex}
              enabled={
                viewerToolMode === 'properties' &&
                propertiesToolMode === 'angle-bias' &&
                !selectedBoneExcluded
              }
              poseVersion={poseVersion}
              propertiesVersion={propertiesVersion}
              manipulatorScale={manipulatorScale}
              onBonePropertiesChange={onBonePropertiesChange}
              onDraggingChange={onPropertiesManipulatorDraggingChange}
            />
            <JointOffsetManipulator
              skeleton={skeleton}
              mapping={mapping}
              selectedBoneIndex={selectedBoneIndex}
              enabled={
                viewerToolMode === 'properties' &&
                propertiesToolMode === 'offset' &&
                !selectedBoneExcluded
              }
              poseVersion={poseVersion}
              manipulatorScale={manipulatorScale}
              onJointOffsetChange={onJointOffsetChange}
              onDraggingChange={onPropertiesManipulatorDraggingChange}
            />
          </>
        )}
      </group>
    </>
  );
}

function ScreenshotCapture({
  screenshotRef,
}: {
  screenshotRef?: React.MutableRefObject<(() => Promise<Blob>) | null>;
}) {
  const gl = useThree((state) => state.gl);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    if (!screenshotRef) return;
    screenshotRef.current = () =>
      new Promise<Blob>((resolve, reject) => {
        const canvas = gl.domElement;
        if (!canvas) {
          reject(new Error('No canvas'));
          return;
        }
        try {
          raf.current = requestAnimationFrame(() => {
            raf.current = null;
            void captureThumbnailBlob(canvas).then(resolve).catch(reject);
          });
        } catch (e) {
          reject(e);
        }
      });
    return () => {
      if (raf.current != null) cancelAnimationFrame(raf.current);
      screenshotRef.current = null;
    };
  }, [gl, screenshotRef]);

  return null;
}

function ViewerCanvasUnavailableFallback() {
  return (
    <div className="bg-gray-15/45 dark:bg-gray-90/40 absolute inset-0 flex items-center justify-center p-6">
      <div className="dark:bg-gray-90/70 rounded-2xl bg-white/70 p-4 text-center backdrop-blur-md">
        <Text as="div" token="14/medium" className="text-primary">
          3D viewer unavailable
        </Text>
        <Text as="p" token="14" className="text-secondary mt-1 max-w-sm">
          WebGL could not be started. Try reloading the page or checking browser
          GPU/WebGL settings.
        </Text>
      </div>
    </div>
  );
}

function probeWebGLSupport(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }
  try {
    const probeCanvas = document.createElement('canvas');
    const context =
      probeCanvas.getContext('webgl2') ||
      probeCanvas.getContext('webgl') ||
      probeCanvas.getContext('experimental-webgl');
    return Boolean(context);
  } catch {
    return false;
  }
}

type Viewer3DProps = {
  gltfScene: Group | null;
  skeleton?: Skeleton | null;
  mapping?: MappingResult | null;
  autoFrameReady?: boolean;
  autoFrameToken?: number;
  focusRequest?: ViewerFocusRequest | null;
  cameraFollowMotion?: boolean;
  assignedBoneIndexSet?: Set<number> | null;
  excludedBoneRefs?: string[];
  visibility: ViewerVisibility;
  boneColorPreset?: BoneColorPreset;
  boneRadiusScale?: number;
  chainThicknessScale?: number;
  manipulatorScale?: number;
  propertiesVersion?: number;
  poseVersion?: number;
  gridCellSize?: number;
  selectedBoneIndex?: number | null;
  forcedRoleTag?: {
    boneIndex: number;
    role: string;
    confidence: number | null;
  } | null;
  boneAssignmentsViewerFocus?: BoneAssignmentsViewerFocus | null;
  hoveredBoneAssignmentChain?: HoveredBoneAssignmentChain | null;
  boneAssignmentsViewerPickMode?: BoneAssignmentsViewerPickMode | null;
  viewerToolMode?: ViewerToolMode;
  rotateSpaceMode?: RotateSpaceMode;
  propertiesToolMode?: PropertiesToolMode;
  onSelectBone?: (index: number | null) => void;
  onSelectBoneFromPropertiesBadge?: (index: number) => void;
  onBonePropertiesChange?: () => void;
  onJointOffsetChange?: (
    boneRef: string,
    rotation: [number, number, number] | null,
  ) => void;
  onApplyBoneRotateFromWorldQuaternion?: (
    boneIndex: number,
    worldQuaternion: THREE.Quaternion,
  ) => void;
  onApplyBoneRotateFromGimbalDelta?: (
    boneIndex: number,
    axis: GizmoRotationAxis,
    deltaDegrees: number,
  ) => void;
  onSetBoneEulerRotation?: (
    boneIndex: number,
    eulerDegrees: [number, number, number],
  ) => void;
  onRotateGizmoDraggingChange?: (isDragging: boolean) => void;
  onPropertiesManipulatorDraggingChange?: (isDragging: boolean) => void;
  forceTransitionBlur?: boolean;
  suppressBoneNameLabels?: boolean;
  screenshotRef?: React.MutableRefObject<(() => Promise<Blob>) | null>;
};

export default function Viewer3D({
  gltfScene,
  skeleton,
  mapping,
  autoFrameReady = true,
  autoFrameToken = 0,
  focusRequest = null,
  cameraFollowMotion = false,
  assignedBoneIndexSet: assignedBoneIndexSetProp,
  excludedBoneRefs = [],
  visibility,
  boneColorPreset = 'charcoal',
  boneRadiusScale = 1,
  chainThicknessScale = 1,
  manipulatorScale = 1,
  propertiesVersion = 0,
  poseVersion = 0,
  gridCellSize = CARTOGRAPHER_AUTO_GRID_CELL_SIZE,
  selectedBoneIndex,
  forcedRoleTag = null,
  boneAssignmentsViewerFocus = null,
  hoveredBoneAssignmentChain = null,
  boneAssignmentsViewerPickMode = null,
  viewerToolMode = 'select',
  rotateSpaceMode = 'local',
  propertiesToolMode = 'limits',
  onSelectBone,
  onSelectBoneFromPropertiesBadge,
  onBonePropertiesChange,
  onJointOffsetChange,
  onApplyBoneRotateFromWorldQuaternion,
  onApplyBoneRotateFromGimbalDelta,
  onSetBoneEulerRotation,
  onRotateGizmoDraggingChange,
  onPropertiesManipulatorDraggingChange,
  forceTransitionBlur = false,
  suppressBoneNameLabels = false,
  screenshotRef,
}: Viewer3DProps) {
  const assignedBoneIndexSet = useMemo(() => {
    if (assignedBoneIndexSetProp !== undefined) return assignedBoneIndexSetProp;
    return buildAssignedBoneIndexSet(mapping ?? null, skeleton ?? null);
  }, [assignedBoneIndexSetProp, mapping, skeleton]);

  const isRotateGizmoDraggingRef = useRef(false);
  const isPropertiesManipulatorDraggingRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const [framedSceneUuid, setFramedSceneUuid] = useState<string | null>(null);
  const [hasCanvasFailure, setHasCanvasFailure] = useState(false);
  const isWebGLAvailable = useMemo(() => probeWebGLSupport(), []);
  const showCanvasFallback = !isWebGLAvailable || hasCanvasFailure;

  useEffect(() => {
    if (!gltfScene || showCanvasFallback) return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const handleContextCreationError = (event: Event) => {
      const targetCanvas = event.target as HTMLCanvasElement | null;
      if (targetCanvas && wrapper.contains(targetCanvas)) {
        setHasCanvasFailure(true);
      }
    };

    wrapper.addEventListener(
      'webglcontextcreationerror',
      handleContextCreationError,
      true,
    );

    return () => {
      wrapper.removeEventListener(
        'webglcontextcreationerror',
        handleContextCreationError,
        true,
      );
    };
  }, [gltfScene, showCanvasFallback]);

  const isFramed = framedSceneUuid === gltfScene?.uuid;
  const showSnapshot = !!snapshotUrl && !isFramed;
  const showBlurOverlay = forceTransitionBlur || showSnapshot;
  const handleRotateManipulatorDraggingChange = useCallback(
    (isDragging: boolean) => {
      isRotateGizmoDraggingRef.current = isDragging;
      onRotateGizmoDraggingChange?.(isDragging);
    },
    [onRotateGizmoDraggingChange],
  );
  const handlePropertiesManipulatorDraggingChange = useCallback(
    (isDragging: boolean) => {
      isPropertiesManipulatorDraggingRef.current = isDragging;
      onPropertiesManipulatorDraggingChange?.(isDragging);
    },
    [onPropertiesManipulatorDraggingChange],
  );

  const prevGltfSceneRef = useRef(gltfScene);
  if (prevGltfSceneRef.current && prevGltfSceneRef.current !== gltfScene) {
    const canvas = wrapperRef.current?.querySelector('canvas');
    if (canvas) {
      setSnapshotUrl(canvas.toDataURL('image/jpeg', 0.85));
    }
  }
  prevGltfSceneRef.current = gltfScene;

  return (
    <div ref={wrapperRef} className="relative h-full min-h-0 w-full">
      {showBlurOverlay &&
        (showSnapshot ? (
          <img
            src={snapshotUrl}
            alt=""
            className="pointer-events-none absolute inset-0 z-10 h-full w-full object-cover"
            style={{ filter: `blur(${SNAPSHOT_FROST_BLUR_PX}px)` }}
          />
        ) : (
          <div
            className="pointer-events-none absolute inset-0 z-10 bg-white/10 dark:bg-black/15"
            style={{ backdropFilter: `blur(${SNAPSHOT_FROST_BLUR_PX}px)` }}
          />
        ))}
      {gltfScene && showCanvasFallback && <ViewerCanvasUnavailableFallback />}
      {gltfScene && !showCanvasFallback && (
        <ErrorBoundary
          fallback={<ViewerCanvasUnavailableFallback />}
          resetKeys={[gltfScene.uuid]}
          onError={() => setHasCanvasFailure(true)}
        >
          <Canvas
            gl={{ preserveDrawingBuffer: true }}
            shadows={{
              enabled: true,
              autoUpdate: true,
              type: PCFSoftShadowMap,
            }}
            onPointerMissed={(event) => {
              if ('button' in event && event.button !== 0) return;
              if (
                isRotateGizmoDraggingRef.current ||
                isPropertiesManipulatorDraggingRef.current
              ) {
                return;
              }
              onSelectBone?.(null);
            }}
          >
            <SceneContent
              autoFrameReady={autoFrameReady}
              autoFrameToken={autoFrameToken}
              scene={gltfScene}
              skeleton={skeleton ?? null}
              mapping={mapping ?? null}
              focusRequest={focusRequest}
              cameraFollowMotion={cameraFollowMotion}
              assignedBoneIndexSet={assignedBoneIndexSet}
              excludedBoneRefs={excludedBoneRefs}
              visibility={visibility}
              boneColorPreset={boneColorPreset}
              boneRadiusScale={boneRadiusScale}
              chainThicknessScale={chainThicknessScale}
              manipulatorScale={manipulatorScale}
              propertiesVersion={propertiesVersion}
              poseVersion={poseVersion}
              gridCellSize={gridCellSize}
              selectedBoneIndex={selectedBoneIndex ?? null}
              forcedRoleTag={forcedRoleTag}
              boneAssignmentsViewerFocus={boneAssignmentsViewerFocus}
              hoveredBoneAssignmentChain={hoveredBoneAssignmentChain}
              boneAssignmentsViewerPickMode={boneAssignmentsViewerPickMode}
              viewerToolMode={viewerToolMode}
              rotateSpaceMode={rotateSpaceMode}
              propertiesToolMode={propertiesToolMode}
              isFramed={isFramed}
              onFrameSettled={() => setFramedSceneUuid(gltfScene.uuid)}
              onSelectBone={onSelectBone}
              onSelectBoneFromPropertiesBadge={onSelectBoneFromPropertiesBadge}
              onBonePropertiesChange={onBonePropertiesChange}
              onJointOffsetChange={onJointOffsetChange}
              onApplyBoneRotateFromWorldQuaternion={
                onApplyBoneRotateFromWorldQuaternion
              }
              onApplyBoneRotateFromGimbalDelta={
                onApplyBoneRotateFromGimbalDelta
              }
              onSetBoneEulerRotation={onSetBoneEulerRotation}
              onRotateGizmoDraggingChange={
                handleRotateManipulatorDraggingChange
              }
              onPropertiesManipulatorDraggingChange={
                handlePropertiesManipulatorDraggingChange
              }
              suppressBoneNameLabels={suppressBoneNameLabels}
            />
            {screenshotRef && (
              <ScreenshotCapture screenshotRef={screenshotRef} />
            )}
          </Canvas>
        </ErrorBoundary>
      )}
    </div>
  );
}
